import { usePersistedState, useScrollMemory } from '../../hooks/usePersistedState';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Space,
  Typography,
  Card,
  Row,
  Col,
  Input,
  DatePicker,
  Select,
  Tooltip,
  Statistic,
} from 'antd';
import ConfigurableTable from '../../components/ConfigurableTable';
import {
  DensityToggle,
  useDensity,
  DocCell,
  PersonCell,
  ProgressCell,
  StatusPill,
  MissingValue,
  FilterChips,
} from '../../components/table-cells';
import {
  InboxOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { listGRNs, getLookups } from '../../api';

const { Title, Text } = Typography;

const num = (v: any) => Number(v || 0);
const fmtQty = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 2 });

/** Chip predicates — all client-side, on the rows this page already loaded. */
const isThisWeek = (g: any) =>
  !!g.receivedOn && dayjs(g.receivedOn).isAfter(dayjs().startOf('day').subtract(7, 'day'));
const hasRejections = (g: any) =>
  num(g.totalRejected) > 0 || (g.lines || []).some((l: any) => num(l.rejectedQty) > 0);
const noDeliveryNote = (g: any) => !g.deliveryNoteRef;
const noInvoice = (g: any) => !g.supplierInvoice;

const ATTENTION_TESTS: Record<string, (g: any) => boolean> = {
  rejections: hasRejections,
  noDeliveryNote,
  noInvoice,
};

const dateRangeCodec = {
  serialize: (v: [Dayjs | null, Dayjs | null] | null) => (v ? [v[0] ? v[0].format('YYYY-MM-DD') : null, v[1] ? v[1].format('YYYY-MM-DD') : null] : null),
  deserialize: (raw: any): [Dayjs | null, Dayjs | null] | null => (raw ? [raw[0] ? dayjs(raw[0]) : null, raw[1] ? dayjs(raw[1]) : null] : null),
};

const GRNList: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = usePersistedState('grns', 'page', 1);

  // Filter state
  const [searchText, setSearchText] = usePersistedState<string>('grns', 'search', '');
  const [filterVendors, setFilterVendors] = usePersistedState<number[]>('grns', 'vendors', []);
  const [dateRange, setDateRange] = usePersistedState<[Dayjs | null, Dayjs | null] | null>('grns', 'dateRange', null, dateRangeCodec);
  useScrollMemory('grns', !loading);
  const [vendorOptions, setVendorOptions] = useState<Array<{ label: string; value: number }>>([]);

  // Row density and the chip row (both client-side, on the loaded page)
  const [density, setDensity] = useDensity('grn-list');
  const compact = density === 'compact';
  const [periodChip, setPeriodChip] = useState<string | null>(null);
  const [attention, setAttention] = useState<string[]>([]);

  // Debounce ref for search
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load vendor lookups once
  useEffect(() => {
    getLookups()
      .then((l) => {
        setVendorOptions(
          (l.vendors || []).map((v: any) => ({ label: v.name, value: v.id }))
        );
      })
      .catch(() => {});
  }, []);

  const fetchGRNs = useCallback(
    async (overridePage?: number) => {
      setLoading(true);
      try {
        const currentPage = overridePage ?? page;
        const params: Record<string, any> = { page: currentPage, limit: 25 };
        if (searchText.trim()) params.search = searchText.trim();
        if (filterVendors.length > 0) params.vendorIds = filterVendors.join(',');
        if (dateRange && dateRange[0]) params.startDate = dateRange[0].toISOString();
        if (dateRange && dateRange[1]) {
          const end = dateRange[1].endOf('day');
          params.endDate = end.toISOString();
        }
        const result = await listGRNs(params);
        setData(result.data);
        setTotal(result.total);
      } catch (err) {
        console.error('Failed to load GRNs', err);
      } finally {
        setLoading(false);
      }
    },
    [page, searchText, filterVendors, dateRange]
  );

  useEffect(() => {
    fetchGRNs();
  }, [fetchGRNs]);

  const handleSearchChange = (value: string) => {
    setSearchText(value);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setPage(1);
    }, 300);
  };

  const handleVendorChange = (values: number[]) => {
    setFilterVendors(values);
    setPage(1);
  };

  const handleDateChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    setDateRange(dates);
    setPage(1);
  };

  // Stats
  const totalGRNs = total;
  const totalAccepted = data.reduce((sum: number, g: any) => sum + (g.totalAccepted || 0), 0);
  const totalRejected = data.reduce((sum: number, g: any) => sum + (g.totalRejected || 0), 0);
  const withReturns = data.filter((g: any) => g.hasReturns).length;

  // Chip counts and the rows the chips leave visible — the loaded page only.
  const chipCounts = useMemo(
    () => ({
      week: data.filter(isThisWeek).length,
      rejections: data.filter(hasRejections).length,
      noDeliveryNote: data.filter(noDeliveryNote).length,
      noInvoice: data.filter(noInvoice).length,
    }),
    [data],
  );

  const visibleRows = useMemo(
    () =>
      data.filter(
        (g: any) =>
          (periodChip === 'week' ? isThisWeek(g) : true) &&
          attention.every((k) => ATTENTION_TESTS[k]?.(g)),
      ),
    [data, periodChip, attention],
  );

  const columns: any[] = [
    {
      title: 'GRN',
      key: 'grnId',
      width: 150,
      render: (_: any, record: any) => (
        <DocCell id={record.grnId} linkTo={`/grns/${record.grnId}`} compact={compact} />
      ),
    },
    {
      title: 'PO · Vendor',
      key: 'poVendor',
      width: 220,
      render: (_: any, record: any) => {
        const vendor = record.poHeader?.vendor;
        const meta = vendor?.name
          ? `${vendor.name}${vendor.appCode ? ` · ${vendor.appCode}` : ''}`
          : null;
        if (!record.poHeader?.poId) return <MissingValue label="no PO" compact={compact} />;
        return (
          <DocCell
            id={record.poHeader.poId}
            linkTo={`/pos/${record.poHeader.id}`}
            meta={meta}
            compact={compact}
          />
        );
      },
    },
    {
      title: 'Received',
      key: 'received',
      width: 160,
      render: (_: any, record: any) => (
        <PersonCell
          name={record.receivedOn ? dayjs(record.receivedOn).format('D MMM YYYY') : null}
          sub={record.receivedBy?.displayName}
          compact={compact}
          missingLabel="no receipt date"
        />
      ),
    },
    {
      title: 'Lines',
      dataIndex: 'lineCount',
      width: 70,
      align: 'center' as const,
    },
    {
      title: 'Accepted / delivered',
      key: 'accepted',
      width: 180,
      render: (_: any, record: any) => (
        <ProgressCell
          done={num(record.totalAccepted)}
          total={num(record.totalDelivered)}
          compact={compact}
        />
      ),
    },
    {
      title: 'Quality',
      key: 'quality',
      width: 130,
      render: (_: any, record: any) => {
        const rejected = num(record.totalRejected);
        return rejected > 0 ? (
          <StatusPill tone="bad" label={`${fmtQty(rejected)} rejected`} />
        ) : (
          <StatusPill tone="ok" label="All accepted" />
        );
      },
    },
    {
      title: 'Documents',
      key: 'documents',
      width: 180,
      render: (_: any, record: any) => {
        const dn = record.deliveryNoteRef ? (
          <span>DN {record.deliveryNoteRef}</span>
        ) : (
          <MissingValue label="no delivery note" compact={compact} />
        );
        const inv = record.supplierInvoice ? (
          <span>Inv {record.supplierInvoice}</span>
        ) : (
          <MissingValue label="no invoice" compact={compact} />
        );
        return compact ? (
          <span>
            {dn} · {inv}
          </span>
        ) : (
          <div style={{ lineHeight: 1.35 }}>
            <div>{dn}</div>
            <div>{inv}</div>
          </div>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_: any, record: any) => (
        <Tooltip title="View GRN">
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/grns/${record.grnId}`)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space align="start">
            <InboxOutlined style={{ fontSize: 24, color: '#1890ff' }} />
            <div>
              <Title level={4} style={{ margin: 0 }}>Goods Receiving</Title>
              <Text type="secondary" style={{ fontSize: 12 }}>{total.toLocaleString('en-US')} GRNs</Text>
            </div>
          </Space>
        </Col>
        <Col>
          <DensityToggle value={density} onChange={setDensity} />
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="Total GRNs" value={totalGRNs} prefix={<InboxOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Accepted (this page)"
              value={totalAccepted}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Rejected (this page)"
              value={totalRejected}
              valueStyle={{ color: totalRejected > 0 ? '#ff4d4f' : undefined }}
              prefix={<CloseCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="With Returns"
              value={withReturns}
              valueStyle={{ color: withReturns > 0 ? '#faad14' : undefined }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Filter bar */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={24} md={8}>
            <Input.Search
              placeholder="Search GRN# or PO#"
              value={searchText}
              onChange={(e) => handleSearchChange(e.target.value)}
              onSearch={() => {
                setPage(1);
                fetchGRNs(1);
              }}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Select
              mode="multiple"
              placeholder="Vendor"
              allowClear
              style={{ width: '100%' }}
              value={filterVendors}
              onChange={handleVendorChange}
              options={vendorOptions}
              maxTagCount="responsive"
              optionFilterProp="label"
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <DatePicker.RangePicker
              style={{ width: '100%' }}
              placeholder={['Received From', 'Received To']}
              value={dateRange as any}
              onChange={(dates) => handleDateChange(dates as [Dayjs | null, Dayjs | null] | null)}
            />
          </Col>
        </Row>
      </Card>

      <FilterChips
        style={{ marginBottom: 12 }}
        allCount={data.length}
        value={periodChip}
        onChange={setPeriodChip}
        chips={[{ key: 'week', label: 'This week', count: chipCounts.week }]}
        attention={[
          { key: 'rejections', label: 'With rejections', count: chipCounts.rejections, tone: 'bad' },
          { key: 'noDeliveryNote', label: 'No delivery note', count: chipCounts.noDeliveryNote, tone: 'warn' },
          { key: 'noInvoice', label: 'No invoice', count: chipCounts.noInvoice, tone: 'warn' },
        ]}
        attentionValue={attention}
        onAttentionChange={setAttention}
      />

      <Card>
        <ConfigurableTable
          tableId="grn-list"
          columns={columns}
          dataSource={visibleRows}
          rowKey="id"
          loading={loading}
          size={compact ? 'small' : 'middle'}
          scroll={{ x: 1200 }}
          pagination={{
            current: page,
            total,
            pageSize: 25,
            showSizeChanger: false,
            onChange: (p) => setPage(p),
            showTotal: (t) => `${t} GRNs`,
          }}
        />
      </Card>
    </div>
  );
};

export default GRNList;
